import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';

export type ConfirmOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isDestructive?: boolean;
};

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Confirm | null>(null);

type Pending = { options: ConfirmOptions; settle: (answer: boolean) => void };

/** Resolves to what was clicked, so a guard reads as one line and no call site
 *  has to hold dialog state. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending | null>(null);

  const confirm = useCallback<Confirm>(
    (options) =>
      new Promise<boolean>((resolve) => {
        setPending({
          options,
          settle: (answer) => {
            setPending(null);
            resolve(answer);
          },
        });
      }),
    [],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            pending?.settle(false);
          }
        }}
      >
        {pending && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{pending.options.title}</AlertDialogTitle>
              {pending.options.description && (
                <AlertDialogDescription>
                  {pending.options.description}
                </AlertDialogDescription>
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => pending.settle(false)}>
                {pending.options.cancelLabel ?? t('Cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                variant={
                  pending.options.isDestructive ? 'destructive' : 'default'
                }
                onClick={() => pending.settle(true)}
              >
                {pending.options.confirmLabel ?? t('Confirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirm = useContext(ConfirmContext);

  if (!confirm) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }

  return confirm;
}
